
import React from 'react';
import { ChartDataModel } from '../types';

// Localized Sign Names (Small/Short format where applicable)
const SIGN_NAMES: Record<number, Record<string, string>> = {
  1: { en: 'aries', ta: 'மேஷம்', si: 'මේෂ' },
  2: { en: 'taurus', ta: 'ரிஷபம்', si: 'වෘෂභ' },
  3: { en: 'gemini', ta: 'மிதுனம்', si: 'මිථුන' },
  4: { en: 'cancer', ta: 'கடகம்', si: 'කටක' },
  5: { en: 'leo', ta: 'சிம்மம்', si: 'සිංහ' },
  6: { en: 'virgo', ta: 'கன்னி', si: 'කන්‍යා' },
  7: { en: 'libra', ta: 'துலாம்', si: 'තුලා' },
  8: { en: 'scorpio', ta: 'விருச்சிகம்', si: 'වෘශ්චික' },
  9: { en: 'sagittarius', ta: 'தனுசு', si: 'ධනු' },
  10: { en: 'capricorn', ta: 'மகரம்', si: 'මකර' },
  11: { en: 'aquarius', ta: 'கும்பம்', si: 'කුම්භ' },
  12: { en: 'pisces', ta: 'மீனம்', si: 'මීන' }
};

const detectLanguage = (text: string): 'ta' | 'si' | 'en' => {
    // Detect language based on Title string
    if (text.includes('ராசி') || text.includes('நவாம்சம்')) return 'ta';
    if (text.includes('රාශි') || text.includes('නවාංශක')) return 'si';
    return 'en';
};

const ChartCell: React.FC<{ signId: number; planets: string[]; border: string; signName: string }> = ({ signId, planets, border, signName }) => {
  // Darker Blue Border for better visibility on darker background
  const borderColor = "border-sky-800/30"; 
  
  const borderClass = `
    ${border.includes('r') ? `border-r ${borderColor}` : ''}
    ${border.includes('b') ? `border-b ${borderColor}` : ''}
  `;

  return (
    <div className={`
      relative flex flex-wrap content-center justify-center items-center 
      ${borderClass} 
      bg-transparent hover:bg-sky-400/10 transition-colors duration-300 p-0.5
    `}>
      {/* Sign Number (Top-Left) - Darker text */}
      <span className="absolute top-0.5 left-1 text-[8px] text-slate-600/80 font-sans font-medium pointer-events-none select-none">
        {signId}
      </span>

      {/* Sign Name (Bottom-Center) - Darker text */}
      <span className="absolute bottom-0.5 w-full text-center text-[8px] text-slate-600/70 font-sans leading-none pointer-events-none select-none tracking-tight px-0.5">
        {signName}
      </span>

      {/* Planets */}
      <div className="relative z-10 flex flex-wrap justify-center gap-x-1 gap-y-0.5 w-full h-full content-center px-0.5 pb-2">
        {planets.map((p, i) => {
          // Highlight Ascendant/Lagna
          const isLagna = ['Asc', 'Lagna', 'La', 'L', 'லக்', 'ல', 'ලග්', 'ල'].some(l => p.startsWith(l));
          
          if (isLagna) {
             return (
                <span key={i} className="text-[9px] sm:text-[10px] font-black px-1 py-0.5 rounded-[2px] bg-rose-600 text-white shadow-sm border border-rose-500 leading-none">
                    {p}
                </span>
             );
          }

          // Highlight Moon (Chandra) - Black Box, White Text
          const isMoon = ['Mo', 'Moon', 'சந்', 'සඳු'].some(m => p.startsWith(m));
          
          if (isMoon) {
             return (
                <span key={i} className="text-[9px] sm:text-[10px] font-black px-1 py-0.5 rounded-[2px] bg-slate-900 text-white shadow-sm border border-slate-700 leading-none">
                    {p}
                </span>
             );
          }
          
          // Nodes (Rahu/Ketu) + Mandi styling - Deep Blue/Purple
          const isNodeOrMandi = ['Ra', 'Ke', 'Rahu', 'Ketu', 'ராகு', 'கேது', 'රාහු', 'කේතු', 'Md', 'Mandi', 'மாந்', 'මාන්'].some(n => p.startsWith(n));
          
          // Planet Text Color - Darker for readability on darker blue
          const textColor = isNodeOrMandi ? 'text-indigo-900' : 'text-slate-900';

          return (
            <span 
              key={i} 
              className={`
                text-[10px] sm:text-[11px] font-bold leading-tight ${textColor}
              `}
            >
              {p}
            </span>
          );
        })}
      </div>
    </div>
  );
};

interface GridProps {
    planets: Record<string, string[]>;
    title: string;
}

const ChartGrid: React.FC<GridProps> = ({ planets, title }) => {
  // Helper to get content for a sign index (1 = Aries, ..., 12 = Pisces)
  const getSignContent = (signId: number) => planets[signId.toString()] || [];

  // Robustly handle the title
  const displayTitle = title.replace(/மண்டலம்|Mandalam/gi, '').trim();
  
  // Detect language
  const lang = detectLanguage(title);
  
  // Helper to get localized sign name
  const getSignName = (id: number) => SIGN_NAMES[id][lang];

  return (
    <div className="flex flex-col items-center group w-full max-w-[320px]">
        {/* Outer Container with Darker Gradient Theme */}
        <div className="relative p-1 rounded-sm bg-gradient-to-b from-sky-400 to-blue-500 shadow-[0_0_15px_rgba(56,189,248,0.2)] w-full">
            {/* Chart Background: Darker Blue (Blue-100) */}
            <div className="aspect-square w-full bg-[#dbeafe] relative select-none text-slate-900 overflow-hidden border border-sky-500 shadow-inner">
                {/* 4x4 Grid for South Indian Chart */}
                <div className="grid grid-cols-4 grid-rows-4 w-full h-full">
                    
                    {/* Row 1 */}
                    <ChartCell signId={12} planets={getSignContent(12)} border="r b" signName={getSignName(12)} />
                    <ChartCell signId={1} planets={getSignContent(1)} border="r b" signName={getSignName(1)} />
                    <ChartCell signId={2} planets={getSignContent(2)} border="r b" signName={getSignName(2)} />
                    <ChartCell signId={3} planets={getSignContent(3)} border="b" signName={getSignName(3)} />

                    {/* Row 2 */}
                    <ChartCell signId={11} planets={getSignContent(11)} border="r b" signName={getSignName(11)} />
                    
                    {/* CENTER AREA */}
                    <div className="col-span-2 row-span-2 flex flex-col items-center justify-center border-r border-b border-sky-800/30 bg-sky-200/30 p-2 text-center relative">
                        <span className="font-serif font-bold text-indigo-950 text-xl md:text-2xl z-10 leading-normal tracking-wide drop-shadow-sm">
                            {displayTitle}
                        </span>
                    </div>

                    <ChartCell signId={4} planets={getSignContent(4)} border="b" signName={getSignName(4)} />

                    {/* Row 3 */}
                    <ChartCell signId={10} planets={getSignContent(10)} border="r b" signName={getSignName(10)} />
                    <ChartCell signId={5} planets={getSignContent(5)} border="b" signName={getSignName(5)} />

                    {/* Row 4 */}
                    <ChartCell signId={9} planets={getSignContent(9)} border="r" signName={getSignName(9)} />
                    <ChartCell signId={8} planets={getSignContent(8)} border="r" signName={getSignName(8)} />
                    <ChartCell signId={7} planets={getSignContent(7)} border="r" signName={getSignName(7)} />
                    <ChartCell signId={6} planets={getSignContent(6)} border="" signName={getSignName(6)} />
                </div>
            </div>
        </div>
    </div>
  );
};

export const SouthIndianCharts: React.FC<{ data: ChartDataModel }> = ({ data }) => {
  if (!data) return null;

  return (
    <div className="flex flex-col md:flex-row gap-6 justify-center items-center w-full my-6 px-1 animate-fade-in">
      <ChartGrid 
        planets={data.rasi} 
        title={data.labels.rasi} 
      />
      <ChartGrid 
        planets={data.navamsa} 
        title={data.labels.navamsa} 
      />
    </div>
  );
};

export default SouthIndianCharts;
